import { HeroStyle2 } from "~/components/HeroStyle2";
import {
  Locale,
  type LocalizedStrings,
  useLocalization,
} from "~/components/localization/LocalizationProvider";
import styles from "./office-tour.module.css";
import { Box, Flex, Heading, Text } from "@radix-ui/themes";
import { HomeConsultForm } from "~/components/home/HomeConsultForm";
import { ImageGallery } from "~/components/office-tour/ImageGallery";

type Localization = {
  title: string;
  introHeading: string;
  introContent: string;
  welcomeTitle: string;
  welcomeP1: string;
  welcomeP2: string;
  convenientTitle: string;
  convenientP1: string;
  convenientP2: string;
};

const englishLocalization: Localization = {
  title: "Office Tour",
  introHeading: "We’re Glad You’re Here!",
  introContent:
    "At Miami Orthodontic Specialists, we believe that an orthodontic office should be designed with the patient in mind.",
  welcomeTitle: "We Look Forward to Meeting You!",
  welcomeP1:
    "We want you to smile the minute you walk through the door! This is why we created a clean, modern space where our patients can feel comfortable.",
  welcomeP2:
    "Each treatment care room has its own privacy divider so that you don't need to look at other patients while your braces or Invisalign are being adjusted. Parents are welcome in these rooms so that they can follow along with the progress of their child's treatment.",
  convenientTitle: "Convenient Care",
  convenientP1:
    "Our office is centrally located near the intersection of 88th Street (Kendall Drive) and 87th Avenue.",
  convenientP2:
    "We are less than a 15-minute drive from the center of Kendall, Pinecrest, South Miami & Palmetto Bay. That means great orthodontic care is only a few minutes away!",
};

const spanishLocalization: Localization = {
  title: "Visita a la Oficina",
  introHeading: "¡Nos alegra que estés aquí!",
  introContent:
    "En Miami Orthodontic Specialists, creemos que un consultorio de ortodoncia debe estar diseñado teniendo al paciente en mente.",
  welcomeTitle: "¡Esperamos conocerte!",
  welcomeP1:
    "¡Queremos que sonrías desde el momento en que cruzas la puerta! Por eso, creamos un espacio limpio y moderno donde nuestros pacientes puedan sentirse cómodos.",
  welcomeP2:
    "Cada sala de tratamiento cuenta con su propio separador de privacidad para que no tenga que mirar a otros pacientes mientras le ajustan los aparatos ortopédicos o Invisalign. Los padres pueden ingresar a estas salas para que puedan seguir el progreso del tratamiento de su hijo.",
  convenientTitle: "Cuidado conveniente",
  convenientP1:
    "Nuestra oficina está ubicada centralmente cerca de la intersección de 88th Street (Kendall Drive) y 87th Avenue.",
  convenientP2:
    "Estamos a menos de 15 minutos en auto del centro de Kendall, Pinecrest, South Miami y Palmetto Bay. ¡Eso significa que la mejor atención de ortodoncia está a solo unos minutos de distancia!",
};

const localized: LocalizedStrings<Localization> = {
  [Locale.EN]: englishLocalization,
  [Locale.ES]: spanishLocalization,
};

export function OfficeTourPage() {
  const getLocalization = useLocalization(localized);

  return (
    <>
      <HeroStyle2
        heroImageUrl="/miami-ortho-3.jpg"
        title={getLocalization("title")}
        backgroundPosition="center"
        marginTop="-25px"
      />
      <Flex
        className={`section ${styles.introSection}`}
        width="100%"
        align="center"
        justify="center"
      >
        <Flex direction="column" gap="7" className={`innerSection`} p="5">
          <Heading as="h3" className={styles.heading} weight="bold">
            {getLocalization("introHeading")}
          </Heading>
          <Text
            size="5"
            style={{
              lineHeight: "1.55em",
              letterSpacing: "normal",
              textWrap: "pretty",
            }}
          >
            {getLocalization("introContent")}
          </Text>
        </Flex>
      </Flex>
      <Flex
        className={`section ${styles.mainContentSectionOuter}`}
        width="100%"
        justify="center"
        align="center"
      >
        <Flex
          gap="7"
          direction="column"
          width="100%"
          px="4"
          className={styles.mainContentSection}
        >
          <Flex gap="6" direction="column" width="100%">
            <Heading as="h3" className={styles.heading}>
              {getLocalization("welcomeTitle")}
            </Heading>
            <Flex gap="4" direction="column" width="100%">
              <Text size="5">{getLocalization("welcomeP1")}</Text>
              <Text size="5">{getLocalization("welcomeP2")}</Text>
            </Flex>
          </Flex>
          <Flex gap="6" direction="column" width="100%">
            <Heading as="h4" className={styles.heading3}>
              {getLocalization("convenientTitle")}
            </Heading>
            <Flex gap="4" direction="column" width="100%">
              <Text size="5">{getLocalization("convenientP1")}</Text>
              <Text size="5">{getLocalization("convenientP2")}</Text>
            </Flex>
          </Flex>
          <Box width="100%">
            <ImageGallery />
          </Box>
        </Flex>
      </Flex>
      <Box className={styles.form}>
        <HomeConsultForm />
      </Box>
    </>
  );
}
